import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Filter, ExternalLink, FileText, Video, Mic, Linkedin, Presentation } from 'lucide-react';

const Insights = () => {
  const [selectedFilter, setSelectedFilter] = useState('All');

  const categories = ['All', 'Research', 'Article', 'Submission', 'Presentation', 'Webinar', 'Video'];

  const featuredResearch = {
    title: 'A Just Energy Transition in the Western Downs and South Burnett Regions',
    description: 'This research addresses a real-world challenge which is urgent and lacking critical investigation: whether or not a just transition is likely for regional Queensland. For communities, decision makers, and industry — a summary of dissertation research completed in 2024, as part of a Masters in Sustainability and Adaptation at the Centre for Alternative Technology.',
    image: 'https://images.squarespace-cdn.com/content/v1/6511255618db8d23005a1257/09a2aa43-d667-412a-8d7d-c54a9cae01a5/Dissertation+Research+Report.jpg',
    ctaText: 'Send me the full research',
    ctaLink: 'mailto:info@sustainify.com.au?subject=Send%20me%20the%20just%20transition%20dissertation'
  };

  const insights = [
    {
      type: 'Submission',
      title: 'Submission to the Consultation on the Climate Active Program',
      description: "Sustainify's response to the Federal consultation on the future of Climate Active. Our position: closing the program without a strong replacement risks a governance vacuum. We propose a strengthened Climate Active 2.0 focused on real emissions reduction and transition performance — not just accounting.",
      date: 'September 2026',
      source: 'Sustainify® · Federal Consultation Submission',
      link: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_live-builder-10/artifacts/iw1zwvoe_Sustainify_Climate_Active_Submission.pdf',
      icon: <FileText size={24} />,
      image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=800'
    },
    {
      type: 'Presentation',
      title: 'Just Transitions for Aboriginal, Torres Strait Islander, and Queensland Communities',
      description: 'Presentation delivered at the Queensland Decarbonisation Forum 2026 — exploring what a genuinely just transition looks like for Aboriginal, Torres Strait Islander, and broader Queensland communities, and how to centre their voices in the energy shift.',
      date: '2026',
      source: 'Queensland Decarbonisation Forum 2026',
      link: 'mailto:info@sustainify.com.au?subject=Request%3A%20QLD%20Decarbonisation%20Forum%202026%20slides&body=Hi%20Sustainify%20team%2C%0A%0AI%27d%20like%20a%20copy%20of%20the%20%22Just%20Transitions%20for%20Aboriginal%2C%20Torres%20Strait%20Islander%2C%20and%20Queensland%20Communities%22%20presentation%20from%20the%20Queensland%20Decarbonisation%20Forum%202026.%0A%0AName%3A%0AOrganisation%3A%0A%0AThanks%21',
      icon: <Presentation size={24} />,
      image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=800'
    },
    {
      type: 'Article',
      title: 'Leading in Times of Division: Catalytic Leadership for Systems Change',
      description: 'LinkedIn article by our Founder & Principal Consultant exploring how catalytic leadership can drive systems change in times of division and uncertainty.',
      date: 'LinkedIn Pulse',
      source: 'LinkedIn Article',
      link: 'https://www.linkedin.com/pulse/leading-times-division-catalytic-leadership-systems-rachelle-ynnwe/',
      icon: <FileText size={24} />,
      image: 'https://customer-assets.emergentagent.com/job_live-builder-10/artifacts/arh4v5su_Leadership%20Visual%20Concepts%20Convergence%20%26%20Catalyst.png'
    },
    {
      type: 'Article',
      title: 'Data Framing for Climate Solutions: An Oceans Case Study',
      description: 'LinkedIn article by our Founder & Principal Consultant exploring how data framing shapes effective climate solutions — drawing on an oceans case study.',
      date: 'LinkedIn Pulse',
      source: 'LinkedIn Article',
      link: 'https://www.linkedin.com/pulse/data-analysis-climate-solutions-importance-diving-sea-rachelle-escpe/',
      icon: <FileText size={24} />,
      image: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=800'
    },
    {
      type: 'Article',
      title: 'Positive Legacies for People — Infrastructure Sustainability Conference Panel',
      description: 'A LinkedIn post on the panel chaired by our Founder & Principal Consultant, "Positive Legacies for People," at the Infrastructure Sustainability Conference, exploring social value, systems change, and legacy impact.',
      date: 'June 2026',
      source: 'Infrastructure Sustainability Conference',
      link: 'https://www.linkedin.com/posts/rachellecooperkulkarni_socialvalue-systemschange-legacyimpact-ugcPost-7468141391722971136-A--3/',
      icon: <FileText size={24} />,
      image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800'
    },
    {
      type: 'Article',
      title: 'Competing in a Carbon-Constrained World: Climate Strategy as Competitive Advantage',
      description: 'Article published on The Strategy Brief exploring how climate strategy creates competitive advantage in a carbon-constrained world.',
      date: '14 August 2025',
      source: 'The Strategy Brief',
      link: 'https://www.thestrategybrief.com/post/competing-in-a-carbon-constrained-world-climate-strategy-as-competitive-advantage',
      icon: <FileText size={24} />,
      image: 'https://images.unsplash.com/photo-1531321538006-82b62d9fbb1f'
    },
    {
      type: 'Webinar',
      title: 'Enhancing Corporate Cultural Capability to Deliver Better Indigenous Energy Outcomes',
      description: '"You need to ask yourself \'what can you give first?\', before seeking anything back on what you\'re doing." — Webinar as part of the Energy Charter\'s First Nations Community of Practice.',
      date: '31 October 2024',
      source: 'Energy Charter - First Nations Community of Practice',
      link: 'https://theenergycharter.my.canva.site/first-nations-better-practice-community-engagement-toolkit',
      icon: <Mic size={24} />,
      image: 'https://images.unsplash.com/photo-1527525443983-6e60c75fff46'
    },
    {
      type: 'Video',
      title: 'Sustainable Industrial Manufacturing Asia Pacific Conference Presentation 2024',
      description: 'Our Founder & Principal Consultant presents at SIMPAC Live: Conversations from the Edge — exploring sustainable industrial manufacturing.',
      date: '2024',
      source: 'SIMPAC Live Conference',
      link: 'https://www.youtube.com/watch?v=d4WeZTkv1iQ&t=1s',
      icon: <Video size={24} />,
      image: 'https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9'
    },
    {
      type: 'Video',
      title: 'Understanding Impact & Legacy - Power Hour Renewable Energy Virtual Forum',
      description: '2024 Power Hour Renewable Energy Virtual Forum focused on understanding impact and legacy in renewable energy. (Featured from 50:18)',
      date: '14 November 2024',
      source: 'Engaged Outcomes - Power Hour Forum',
      link: 'https://www.youtube.com/watch?v=Nf7hcIhbkTE',
      icon: <Video size={24} />,
      image: 'https://images.pexels.com/photos/414905/pexels-photo-414905.jpeg'
    },
    {
      type: 'Research',
      title: 'A Just Energy Transition in the Western Downs and South Burnett Regions',
      description: 'Research addressing whether a just transition is likely for regional Queensland. A summary of Masters dissertation research completed in 2024 — for communities, decision makers, and industry.',
      date: 'November 2024',
      source: 'Sustainify® Research Report',
      link: 'https://www.sustainify.com.au/s/2024_Cooper-Kulkarni_A-just-regional-energy-transition-research-report-p4ks.pdf',
      icon: <FileText size={24} />,
      image: 'https://images.squarespace-cdn.com/content/v1/6511255618db8d23005a1257/09a2aa43-d667-412a-8d7d-c54a9cae01a5/Dissertation+Research+Report.jpg'
    }
  ];

  const filteredInsights = selectedFilter === 'All' 
    ? insights 
    : insights.filter(insight => insight.type === selectedFilter);

  return (
    <div data-testid="insights-page" className="bg-ozone-white min-h-screen">
      <Helmet>
        <title>Insights & Thought Leadership | Sustainify® | Rachelle Cooper Kulkarni</title>
        <meta name="description" content="Insights, research, articles and thought leadership from Sustainify® and Rachelle Cooper Kulkarni — climate strategy, just energy transition, social impact, Indigenous engagement, sustainability." />
        <link rel="canonical" href="https://www.sustainify.com.au/insights" />
      </Helmet>
      {/* Hero */}
      <div 
        className="relative bg-basalt-carbon text-ozone-white py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(26, 28, 30, 0.85), rgba(26, 28, 30, 0.85)), url(https://images.unsplash.com/photo-1527873722743-67759f0854aa)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="insights-title">
            Let's Empower <span className="text-solar-kinetic">Each Other</span>
          </h1>
          <p className="text-xl text-ozone-white/80 max-w-3xl">
            Knowledge is power. It's where Insights become Impact.
          </p>
        </div>
      </div>

      {/* Featured Research */}
      <section className="py-16 bg-white" data-testid="featured-research-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <a 
                href="https://www.sustainify.com.au/s/2024_Cooper-Kulkarni_A-just-regional-energy-transition-research-report-p4ks.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="featured-research-image-link"
              >
                <img 
                  src={featuredResearch.image}
                  alt={featuredResearch.title}
                  className="rounded-2xl shadow-2xl hover:shadow-3xl transition-all w-full"
                />
              </a>
            </div>
            <div>
              <span className="inline-block bg-solar-kinetic text-basalt-carbon px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wide mb-4">
                Featured Research
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-basalt-carbon mb-4 leading-tight">
                A Just Energy Transition.
              </h2>
              <p className="text-basalt-carbon/70 text-lg leading-relaxed mb-6">
                {featuredResearch.description}
              </p>
              <a
                href={featuredResearch.ctaLink}
                data-testid="featured-research-cta"
                className="inline-flex items-center space-x-2 bg-kabi-teal text-white px-6 py-3 rounded-full font-semibold hover:bg-kabi-teal/90 transition-all"
              >
                <span>{featuredResearch.ctaText}</span>
                <ExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <div className="bg-white border-t border-b border-basalt-carbon/10 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4 overflow-x-auto">
            <Filter size={20} className="text-basalt-carbon flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedFilter(category)}
                data-testid={`filter-${category.toLowerCase()}`}
                className={`px-4 py-2 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
                  selectedFilter === category
                    ? 'bg-kabi-teal text-white'
                    : 'bg-basalt-carbon/5 text-basalt-carbon hover:bg-basalt-carbon/10'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Insights */}
      <section className="py-16 bg-ozone-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-basalt-carbon mb-12 text-center">
            Recent <span className="text-kabi-teal">Insights</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredInsights.map((insight, index) => (
              <a 
                key={index}
                href={insight.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`insight-card-${index}`}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:transform hover:scale-[1.02] cursor-pointer group flex flex-col"
              >
                <div className="h-48 overflow-hidden relative">
                  <img 
                    src={insight.image}
                    alt={insight.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-kabi-teal text-white p-2 rounded-lg">
                    {insight.icon}
                  </div>
                </div>
                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-kabi-teal uppercase tracking-wide">
                      {insight.type}
                    </span>
                    <span className="text-xs text-basalt-carbon/60">{insight.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-basalt-carbon mb-3 leading-tight group-hover:text-kabi-teal transition-colors">
                    {insight.title}
                  </h3>
                  <p className="text-basalt-carbon/70 text-sm leading-relaxed mb-4 flex-grow">
                    {insight.description}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-basalt-carbon/10">
                    <span className="text-xs text-basalt-carbon/60 italic">{insight.source}</span>
                    <ExternalLink size={16} className="text-kabi-teal" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* LinkedIn Follow Section */}
      <section className="py-16 bg-kabi-teal/5" data-testid="linkedin-follow-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-kabi-teal to-kabi-teal/80 rounded-3xl p-10 md:p-12 text-ozone-white text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-solar-kinetic/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-solar-kinetic/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-ozone-white rounded-full mb-6">
                <Linkedin size={32} className="text-kabi-teal" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Follow Us on <span className="text-solar-kinetic">LinkedIn</span>
              </h2>
              <p className="text-lg text-ozone-white/90 mb-8 max-w-2xl mx-auto">
                Stay connected with the latest insights, thought leadership, and sustainability updates from Sustainify®.
              </p>
              <div className="flex justify-center">
                <a
                  href="https://www.linkedin.com/company/sustainify-group/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="linkedin-company-cta"
                  className="inline-flex items-center justify-center space-x-2 bg-ozone-white text-kabi-teal px-8 py-4 rounded-full font-semibold hover:bg-solar-kinetic hover:text-basalt-carbon transition-all"
                >
                  <Linkedin size={20} />
                  <span>Follow Sustainify® on LinkedIn</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 bg-basalt-carbon text-ozone-white" data-testid="quote-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl md:text-3xl font-light italic leading-relaxed mb-6">
            "You cannot get through a single day without having an impact on the world around you. 
            <span className="text-solar-kinetic font-semibold not-italic"> What you do makes a difference</span>, 
            and you have to decide what kind of difference you want to make."
          </blockquote>
          <cite className="text-ozone-white/70 text-lg not-italic">— Dr. Jane Goodall</cite>
        </div>
      </section>
    </div>
  );
};

export default Insights;
